import { useCallback, useEffect, useRef } from 'react';
import { Animated, Easing } from 'react-native';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { tokens } from '../../theme/tokens';

interface EntranceAnimationOptions {
  delay?: number;
  duration?: number;
  translateY?: number;
  /** Initial scale to settle from (e.g. 0.96 for a subtle pop). Defaults to 1 (no scaling). */
  scaleFrom?: number;
  autoStart?: boolean;
}

// Ease-out-expo settle — lands softer and more premium than cubic.
const ENTRANCE_EASING = Easing.bezier(0.16, 1, 0.3, 1);

export function useEntranceAnimation(options: EntranceAnimationOptions = {}) {
  const { delay = 0, duration = 600, translateY = 30, scaleFrom = 1, autoStart = true } = options;
  const reducedMotion = useReducedMotion();

  const opacity = useRef(new Animated.Value(reducedMotion ? 1 : 0)).current;
  const translateYAnim = useRef(new Animated.Value(reducedMotion ? 0 : translateY)).current;
  const scaleAnim = useRef(new Animated.Value(reducedMotion ? 1 : scaleFrom)).current;

  const animate = useCallback(() => {
    if (reducedMotion) {
      opacity.setValue(1);
      translateYAnim.setValue(0);
      scaleAnim.setValue(1);
      return;
    }
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration,
        delay,
        useNativeDriver: true,
        easing: ENTRANCE_EASING,
      }),
      Animated.timing(translateYAnim, {
        toValue: 0,
        duration,
        delay,
        useNativeDriver: true,
        easing: ENTRANCE_EASING,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration,
        delay,
        useNativeDriver: true,
        easing: ENTRANCE_EASING,
      }),
    ]).start();
  }, [opacity, translateYAnim, scaleAnim, duration, delay, reducedMotion]);

  const reset = useCallback(() => {
    if (reducedMotion) {
      opacity.setValue(1);
      translateYAnim.setValue(0);
      scaleAnim.setValue(1);
      return;
    }
    opacity.setValue(0);
    translateYAnim.setValue(translateY);
    scaleAnim.setValue(scaleFrom);
  }, [opacity, translateYAnim, scaleAnim, translateY, scaleFrom, reducedMotion]);

  useEffect(() => {
    if (autoStart) {
      animate();
    }
  }, [autoStart, animate]);

  return {
    style: {
      opacity,
      transform: [{ translateY: translateYAnim }, { scale: scaleAnim }],
    },
    animate,
    reset,
  };
}

interface ScaleAnimationOptions {
  initialScale?: number;
  pressedScale?: number;
  duration?: number;
}

export function useScaleAnimation(options: ScaleAnimationOptions = {}) {
  const { initialScale = 1, pressedScale = 0.97, duration = tokens.duration.instant } = options;
  const reducedMotion = useReducedMotion();

  const scale = useRef(new Animated.Value(reducedMotion ? 1 : initialScale)).current;
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (!reducedMotion) return;
    scale.setValue(1);
    opacity.setValue(1);
  }, [opacity, reducedMotion, scale]);

  const onPressIn = useCallback(() => {
    if (reducedMotion) {
      scale.setValue(1);
      opacity.setValue(0.92);
      return;
    }
    opacity.setValue(1);
    Animated.timing(scale, {
      toValue: pressedScale,
      duration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [duration, opacity, pressedScale, reducedMotion, scale]);

  const onPressOut = useCallback(() => {
    if (reducedMotion) {
      scale.setValue(1);
      opacity.setValue(1);
      return;
    }
    Animated.timing(scale, {
      toValue: initialScale,
      duration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [duration, initialScale, opacity, reducedMotion, scale]);

  return {
    style: {
      opacity,
      transform: [{ scale }],
    },
    onPressIn,
    onPressOut,
  };
}

interface PulseAnimationOptions {
  minOpacity?: number;
  maxOpacity?: number;
  duration?: number;
}

export function usePulseAnimation(options: PulseAnimationOptions = {}) {
  const { minOpacity = 0.4, maxOpacity = 1, duration = 1000 } = options;
  const reducedMotion = useReducedMotion();

  const opacity = useRef(new Animated.Value(maxOpacity)).current;

  useEffect(() => {
    if (reducedMotion) {
      opacity.setValue(maxOpacity);
      return;
    }
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: minOpacity,
          duration: duration / 2,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
        Animated.timing(opacity, {
          toValue: maxOpacity,
          duration: duration / 2,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
      ]),
    );
    pulse.start();

    return () => pulse.stop();
  }, [opacity, minOpacity, maxOpacity, duration, reducedMotion]);

  return { opacity };
}
